import { buttonStyle } from 'components/buttons/buttonStyle';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { infoLabel, spansRow } from 'components/GridItemList';
import Loader from 'components/Loader';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import AddressModal from 'components/modals/address/AddressModal';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { Button } from 'components/ui/button';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { CUSTOMIZE_INVOICE_PERMISSIONS } from 'pages/matters/form-sections/InvoiceSpecFields';
import { getLinks } from 'pages/matters/util.tsx';
import { useCallback, useContext } from 'react';
import { FieldValues } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useGetInvoiceSpecTemplatesQuery, useGetInvoiceTemplatesQuery } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import { AddressDto, ContactDto, ContactMinDto, RelationshipTypeCode } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { getAddress, getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { BillingSectionId, specIncludedOption } from '../contactFormMapping';
import { getPaymentNotificationReceivers, getSecondaryRecipientContacts } from '../contactRelationUtils';
import { ContactSection } from '../form-sections/ContactSection';
import { AddressEditPencil, employeeChips, linkChips, renderWebsiteLink } from '../util.tsx';

interface Props {
    contactId?: string;
    contact: ContactDto;
    classifiers: Record<string, ClassifierData>;
    isEinvoicingEnabled: boolean;
    updateAddress: (addressField: string, address: AddressDto) => Promise<void>;
}

type AddressType = 'billingAddress' | 'billingLocalAddress';

// `entity-link` is a targeting hook: the detail row colours it as a link at rest (index.css).
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

const BillingView: React.FC<Props> = ({ contact, classifiers, isEinvoicingEnabled, updateAddress }) => {
    const isNew = useIsNewDesign();
    const missingFields = useContext(DraftActionsContext)?.draft?.missingFields;
    const lastNameFirst = useLastNameFirst();
    const isInvoicingInfoEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_INFO_ENABLED);
    const isInvoicingDateOnPartyFormEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_DATE_ON_PARTY_FORM);
    const isBankAccountSelectionEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_PAYMENT_ACCOUNT_NUMBER);
    const isLocalizedFieldsEnabled = useIsFeatureEnabled([
        FeatureFlag.LOCALIZED_FIELDS,
        FeatureFlag.LOCALIZED_FIELDS_CONTACTS,
    ]);

    const isInvoiceCustomizationEnabled = useIsFeatureEnabled(FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE);
    const canCustomizeInvoice = useHasPermissions(CUSTOMIZE_INVOICE_PERMISSIONS);
    const hasCustomizeButton = isNew && contact.isEditable && isInvoiceCustomizationEnabled && canCustomizeInvoice;
    const { data: invoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();
    const invoiceTemplateName =
        invoiceTemplates?.find((template) => template.id === contact.invoiceTemplateId)?.name || '-';

    const { openModal, closeModal } = useModal();
    const openAddressModal = useCallback(
        (addressType: AddressType): void => {
            openModal(
                <AddressModal
                    onChange={(value) => {
                        void updateAddress(addressType, value);
                    }}
                    initialData={{
                        address1: contact?.[addressType]?.address1 ?? '',
                        address2: contact?.[addressType]?.address2 ?? '',
                        cityName: contact?.[addressType]?.cityName ?? '',
                        countryCode:
                            (
                                getValidOptionsFromClassifier(classifiers['COUNTRY']) as {
                                    label: string;
                                    value: string;
                                }[]
                            ).find((val) => val.value === contact?.[addressType]?.countryCode) ?? null,
                        zipCode: contact?.[addressType]?.zipCode ?? '',
                        additionalInformation: contact?.[addressType]?.additionalInformation ?? '',
                    }}
                    onClose={closeModal}
                    classifiers={classifiers}
                    isBillingAddress={addressType === 'billingAddress' || addressType === 'billingLocalAddress'}
                    sourceAddressForCopy={
                        contact?.partyType === 'COMPANY' ? contact?.registrationAddress : contact?.homeAddress
                    }
                    partyType={contact?.partyType}
                />,
                closeModal
            );
        },
        [classifiers, contact, closeModal, openModal, updateAddress]
    );

    const getAddressText = (key: string) => {
        if (!contact) return '-';
        const address = (contact?.[key as keyof ContactDto] as AddressDto) ?? null;
        const countryLabel =
            classifiers?.['COUNTRY'] && address
                ? getClassifierText(classifiers?.['COUNTRY'], address?.countryCode)
                : undefined;
        return getAddress(address as AddressDto, countryLabel);
    };

    const getEditAddressButton = (addressType: AddressType) => {
        if (!contact) return <div />;
        // A draft is edited in its form only.
        if (contact.isDraft) return undefined;

        return (
            <div className="flex items-center justify-between">
                <Button
                    {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'bg-transparent')}
                    onClick={() => {
                        openAddressModal(addressType);
                    }}
                >
                    <FormattedMessage id="edit" />
                </Button>
            </div>
        );
    };

    // The new design puts the address's Edit inline after it, as the General tab does. Its card is titled
    // with the billing address's own label, so that row keeps the label for assistive technology only.
    const addressRow = (addressType: AddressType, labelId: string) => {
        const content = contact[addressType] ? getAddressText(addressType) : '-';
        const label = <FormattedMessage id={labelId} />;
        return {
            label: isNew && addressType === 'billingAddress' ? <span className="sr-only">{label}</span> : label,
            card: 'billing-address',
            ...(isNew
                ? {
                      content: (
                          <>
                              {content}
                              <AddressEditPencil onClick={() => openAddressModal(addressType)} />
                          </>
                      ),
                      // A set one has a row of its own, as its field has in the section's form.
                      wide: spansRow(content),
                  }
                : { button: getEditAddressButton(addressType), content }),
        };
    };

    // The new design's rows follow the billing form: its labels, its order within a card, a text area's value on its
    // lines, a multi-select's values as its chips and a picker's contact on one line. The form asks a company
    // alone about VAT, and takes its number and VAT country only while it is liable.
    const isCompany = contact.partyType === 'COMPANY';
    const notVatLiable = (!contact.vatRegNum && !contact.vatCountryCode) || !contact.isVatLiable;
    const label = (labelId: string, hintId: string) =>
        isNew ? infoLabel(labelId, hintId) : <FormattedMessage id={labelId} />;
    const partyLine = (party: ContactMinDto | null) =>
        !party?.id ? (
            '-'
        ) : isNew ? (
            <EllipsisTooltip>
                <PartyName party={party} isLink variant="readonly-self" singleLine hideTitle />
            </EllipsisTooltip>
        ) : (
            <PartyName party={party} isLink variant="readonly-self" />
        );
    const secondaryRecipients = getSecondaryRecipientContacts(contact.relations ?? null);
    const paymentDeadlineRow = {
        content: contact.invoicingPaymentDays ?? '-',
        label: <FormattedMessage id="financial-conditions.payment-deadline" />,
        card: 'billing-vat',
        hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
    };
    const bankAccountRows = isBankAccountSelectionEnabled
        ? [
              {
                  content: contact.preferredBankAccount?.name ?? '-',
                  label: <FormattedMessage id="contacts.preferred-bank-account" />,
                  card: 'billing-vat',
                  hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
              },
          ]
        : [];

    const data = [
        {
            content: contact?.isInternalClient ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />,
            label: <FormattedMessage id="contacts.internal-client" />,
            card: 'billing-vat',
        },
        {
            label: (
                <FormattedMessage
                    id={isNew ? 'contacts.about.client-reference' : 'contacts.billing.client-reference'}
                />
            ),
            card: 'billing-vat',
            content: contact?.clientReference ?? '-',
        },
        addressRow('billingAddress', 'billing-address'),
        ...(isLocalizedFieldsEnabled ? [addressRow('billingLocalAddress', 'billing-address-local')] : []),

        {
            content: contact.preferredLanguageCode
                ? getClassifierText(classifiers['LANGUAGE'], contact.preferredLanguageCode)
                : '-',
            label: <FormattedMessage id="contacts.billing.invoice-lang" />,
            card: 'billing-delivery',
        },
        ...(contact.partyType === 'COMPANY' && isEinvoicingEnabled
            ? [
                  {
                      content: contact.einvoiceOperatorType
                          ? t(`einvoiceOperatorType.${contact.einvoiceOperatorType}`)
                          : '-',
                      label: <FormattedMessage id="einvoiceOperatorType.title" />,
                      card: 'billing-delivery',
                  },
                  {
                      content: contact.einvoiceAddress ?? '-',
                      label: <FormattedMessage id="einvoiceAddress.title" />,
                      card: 'billing-delivery',
                  },
                  {
                      content: contact.einvoiceOperatorType === 'PEPPOL' ? '-' : (contact.einvoiceOperatorCode ?? '-'),
                      label: <FormattedMessage id="einvoiceOperatorCode.title" />,
                      card: 'billing-delivery',
                      hidden: contact.einvoiceOperatorType === 'PEPPOL',
                  },
              ]
            : []),
        {
            // The cell keeps the line the form's Customize button takes under the select, so the rows below stay
            // where they are when the card edits.
            content: hasCustomizeButton ? (
                <div className="min-h-12">
                    <EllipsisTooltip>{invoiceTemplateName}</EllipsisTooltip>
                </div>
            ) : (
                invoiceTemplateName
            ),
            label: <FormattedMessage id="matters.invoice-template" />,
            card: 'billing-delivery',
        },
        {
            content:
                invoiceSpecTemplates?.find((template) => template.id === contact.invoiceSpecTemplateId)?.name || '-',
            label: <FormattedMessage id="matters.invoice-specification-template" />,
            card: 'billing-delivery',
        },
        {
            content: specIncludedOption(contact.isSpecIncludedInInvoice ?? null)?.label ?? '-',
            label: <FormattedMessage id="invoices.general-tab.details.merge-invoice-and-spec" />,
            card: 'billing-delivery',
        },
        {
            content: (isNew && isCompany ? !notVatLiable : contact.isVatLiable) ? (
                <FormattedMessage id="yes" />
            ) : (
                <FormattedMessage id="no" />
            ),
            label: <FormattedMessage id="is-vat-liable" />,
            card: 'billing-vat',
            readOnly: !isCompany,
        },
        {
            content: contact.vatRegNum ?? '-',
            label: <FormattedMessage id="vat-number" />,
            card: 'billing-vat',
            // A number stored on a company that is not liable stays under the fields until the form says it is
            // liable, which brings the number's own field.
            readOnly: !isCompany || (notVatLiable && ((form: FieldValues) => !!form.notVatLiable)),
            hidden: isNew && isCompany && !contact.vatRegNum,
        },
        {
            content: contact.vatCountryCode ? getClassifierText(classifiers['COUNTRY'], contact.vatCountryCode) : '-',
            label: <FormattedMessage id="vat-country" />,
            card: 'billing-vat',
            readOnly: !isCompany || (notVatLiable && ((form: FieldValues) => !!form.notVatLiable)),
            hidden: isNew && isCompany && !contact.vatCountryCode,
        },
        ...(isNew ? [...bankAccountRows, paymentDeadlineRow] : [paymentDeadlineRow, ...bankAccountRows]),
        {
            content: contact.billingNotes ?? '-',
            label: label('contacts.billing-notes', 'contacts.billing-notes-tooltip'),
            card: 'billing-rules',
            multiline: true,
        },
        ...(isInvoicingInfoEnabled
            ? [
                  {
                      content: contact?.invoicingInfo ?? '-',
                      label: label('contacts.invoicing-info-internal', 'contacts.invoicing-info-tooltip'),
                      card: 'billing-rules',
                      multiline: true,
                  },
              ]
            : []),
        {
            content: partyLine(contact.payerParty),
            label: label('contacts.billing.payer-party', 'contacts.billing.payer-party-tooltip'),
            card: 'billing-vat',
        },
        {
            content: contact.useJointInvoicing ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />,
            label: <FormattedMessage id="contacts.billing.use-joint-invoicing" />,
            card: 'billing-rules',
        },
        ...(isInvoicingDateOnPartyFormEnabled
            ? [
                  {
                      content: contact.issueInvoicesWithCurrentDate ? (
                          <FormattedMessage id="yes" />
                      ) : (
                          <FormattedMessage id="no" />
                      ),
                      label: label(
                          'contacts.billing.issue-invoices-with-current-date',
                          'contacts.tooltips.issue-invoices-with-current-date'
                      ),
                      card: 'billing-rules',
                  },
              ]
            : []),
        {
            content: partyLine(contact.primaryRecipientParty),
            label: label('matters.primary-recipient', 'matters.tooltips.primary-recipient'),
            card: 'billing-recipients',
        },
        {
            content: contact.primaryRecipientAdditionalEmails || '-',
            label: label('matters.primary-recipient-emails', 'matters.tooltips.primary-recipient-emails'),
            card: 'billing-recipients',
            multiline: isNew,
        },
        {
            content: secondaryRecipients.length > 0 ? getLinks('contact', secondaryRecipients) : '-',
            chips: linkChips('contact', secondaryRecipients),
            label: label('matters.secondary-recipient', 'matters.tooltips.secondary-recipient'),
            card: 'billing-recipients',
        },
        {
            content: contact.secondaryRecipientAdditionalEmails || '-',
            label: label('matters.secondary-recipient-emails', 'matters.tooltips.secondary-recipient-emails'),
            card: 'billing-recipients',
            multiline: isNew,
        },
        {
            content: contact.invoicingDeadlineDay || '-',
            label: label('matters.client-invoicing-deadline-day', 'matters.client-invoicing-deadline-tooltip'),
            card: 'billing-rules',
            hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
        },
        {
            content: !contact.externalBillingInfo ? (
                '-'
            ) : isNew ? (
                <span className="whitespace-pre-wrap">{renderWebsiteLink(contact.externalBillingInfo, 600)}</span>
            ) : (
                renderWebsiteLink(contact?.externalBillingInfo, 600)
            ),
            label: label('matters.client-external-billing-info', 'matters.client-external-billing-info-tooltip'),
            card: 'billing-rules',
            hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
            multiline: isNew,
        },
        {
            content: contact.invoiceArchiveEmail || '-',
            label: <FormattedMessage id="contacts.billing.invoice-archive-email" />,
            card: 'billing-delivery',
            hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
            multiline: isNew,
        },
        {
            content:
                contact.paymentNotificationReceivers && contact.paymentNotificationReceivers.length > 0
                    ? contact.paymentNotificationReceivers
                          .map((relation) => relation.employee)
                          .filter((employee) => employee.name)
                          .sort((a, b) => a.name!.localeCompare(b.name!))
                          .map((employee, index, array) => (
                              <span key={employee.id} className="contents">
                                  <RouterLink className={LINK_CLASS} to={`/employee/${employee.id}`}>
                                      {employee.name}
                                  </RouterLink>
                                  {index < array.length - 1 ? ',\u00A0' : ''}
                              </span>
                          ))
                    : '-',
            chips: employeeChips(
                getPaymentNotificationReceivers(contact.paymentNotificationReceivers ?? null),
                lastNameFirst
            ),
            label: <FormattedMessage id="contacts.billing.invoice-payment-notification-to" />,
            card: 'billing-recipients',
            hidden: contact.relationshipTypeCode !== RelationshipTypeCode.CLIENT,
        },
    ];

    if (!contact) {
        return <Loader />;
    }

    if (isNew) {
        const card = (id: BillingSectionId, titleId: string) => {
            const rows = data.filter((row) => row.card === id);
            return (
                <ContactSection id={id} titleId={titleId} contact={contact} rows={rows}>
                    {(edit) => (
                        <GridItemList isNew titleId={titleId} data={withMissing(rows, missingFields)} button={edit} />
                    )}
                </ContactSection>
            );
        };
        // One card per topic, each editing its own fields in place; two columns from 1024 px.
        return (
            <div className="grid grid-cols-1 items-start gap-(--card-gap) md:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {card('billing-vat', 'contacts.billing.vat-title')}
                    {card('billing-delivery', 'contacts.billing.delivery-title')}
                    {card('billing-rules', 'contacts.billing.rules-title')}
                </div>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    {card('billing-address', 'billing-address')}
                    {card('billing-recipients', 'contacts.billing.recipients-title')}
                </div>
            </div>
        );
    }

    return (
        <GridItemList
            titleId="contacts.billing.title"
            size={16}
            data={withMissing(data, missingFields)}
            key="debt-list"
        />
    );
};

export default BillingView;
